// SYSTEM_ROUTE: /reviews
import { useState } from 'react';
import type { ReviewType } from '@life-os/app-core';
import { Button, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { useAsync } from '../lib/useAsync.js';

const TYPES: { value: ReviewType; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ReviewsScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [type, setType] = useState<ReviewType>('daily');
  const [notes, setNotes] = useState('');

  const recent = useAsync(() => services.reviews.listRecent(12), [revision]);
  const items = recent.value ?? [];

  const save = async () => {
    if (notes.trim().length === 0) return;
    await services.reviews.record({ reviewType: type, forDate: todayDate(), notes });
    setNotes('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title="Reviews" subtitle="Turn captured days into decisions — calmly, without shame." />

      <Pane title="New review" hint="What mattered, what slipped, what's next.">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', marginBottom: 'var(--lc-stack-sm)' }}>
          {TYPES.map((t) => (
            <Button
              key={t.value}
              variant={type === t.value ? 'primary' : 'ghost'}
              onClick={() => setType(t.value)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="What went well? What slipped? What's the next small step?"
          style={{
            width: '100%',
            resize: 'vertical',
            background: 'var(--lc-surface-container)',
            color: 'var(--lc-on-surface)',
            border: '1px solid var(--lc-outline-variant)',
            borderRadius: 'var(--lc-radius-lg)',
            padding: '10px 12px',
            font: 'inherit',
            fontSize: 'var(--lc-text-sm)',
          }}
        />
        <div style={{ display: 'flex' }}>
          <Button variant="primary" style={{ marginLeft: 'auto' }} onClick={() => void save()}>
            Save review
          </Button>
        </div>
      </Pane>

      <Pane title="Recent reviews" hint={`${items.length}`}>
        <Stack>
          {items.length === 0 ? (
            <EmptyState title="No reviews yet">A two-minute daily review compounds. Start with today.</EmptyState>
          ) : (
            items.map((r) => (
              <CompactRow
                key={String(r.id)}
                title={r.notes.split('\n')[0] || `${r.reviewType} review`}
                subtitle={`${r.reviewType} · ${r.forDate}`}
              />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
